import {
  Box,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { AcademyArea } from '../../../pages/academy/Academy/AcademyService';
import { StatusDot } from '../AcademyStatus/AcademyStatus';
import { getSkillProgressLabel } from '../Utilities/academyLabels';
import { getAcademyMaskedIconStyle } from '../Utilities/academyMaskedIcon';
import { getAcademyPathIconUrl } from '../Utilities/academyPathIcons';
import { academyStyles } from '../../../pages/academy/Academy/Academy.styles';
import { skillPathsClassNames } from './SkillPaths.styles';

export interface SkillPathsProps {
  area: AcademyArea;
  onOpenSkill: (skillKey: string) => void;
  selectedSkillKey: string | null;
}

export function SkillPaths({
  area,
  onOpenSkill,
  selectedSkillKey,
}: SkillPathsProps) {
  const theme = useTheme();

  return (
    <Box
      component="section"
      className={skillPathsClassNames.root}
      sx={academyStyles.section()}
      aria-labelledby="paths-title"
    >
      <Typography id="paths-title" component="h2" variant="h2" sx={academyStyles.sectionTitle()}>
        Skill Paths with All Lessons
      </Typography>
      <Box className={skillPathsClassNames.grid} sx={academyStyles.cardGrid(theme)}>
        {area.skillPaths.map((path) => {
          const pathIconUrl = getAcademyPathIconUrl(path.pathKey);

          return (
            <Paper
              component="article"
              className={skillPathsClassNames.card}
              sx={academyStyles.pathCard()}
              key={path.id}
              elevation={0}
            >
              <Typography component="h3" sx={academyStyles.pathHeading(theme)}>
                <Box component="span" sx={academyStyles.pathIcon(theme)} aria-hidden="true">
                  <Box
                    component="span"
                    sx={academyStyles.pathIconImage()}
                    style={getAcademyMaskedIconStyle(pathIconUrl)}
                  />
                </Box>
                <Box component="span">{path.title}</Box>
              </Typography>
              <Box
                component="ul"
                className={skillPathsClassNames.skillList}
                sx={academyStyles.pathSkillList()}
              >
                {path.skills.map((skill) => (
                  <Box
                    component="li"
                    className={skillPathsClassNames.skillListItem}
                    key={skill.id}
                    sx={academyStyles.pathSkillListItem()}
                  >
                    <StatusDot
                      learnConfirmedBy={skill.learnConfirmedBy}
                      applyConfirmedBy={skill.applyConfirmedBy}
                      learnRequired={skill.learnRequired}
                      applyRequired={skill.applyRequired}
                    />
                    <Box component="span" className="sr-only">
                      {getSkillProgressLabel(
                        skill.learnConfirmedBy,
                        skill.applyConfirmedBy,
                        skill.learnRequired,
                        skill.applyRequired,
                      )}
                      .
                    </Box>
                    <Button
                      sx={academyStyles.skillPathButton(theme, skill.skillKey === selectedSkillKey)}
                      type="button"
                      disableRipple
                      aria-pressed={skill.skillKey === selectedSkillKey}
                      onClick={() => onOpenSkill(skill.skillKey)}
                    >
                      {skill.title}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
